/**
 * The viewer's byte store: how an open or a prefetch gets a file's bytes, and where OPFS keeps
 * them, scoped to the admitted tenant content scope. Callers import it through
 * `loadFileAndCache.ts`, which also holds logout's whole-cache clear.
 */
import {
    captureTenantScopeBoundary,
    currentTenantContentScopeKey,
    currentTenantScope,
    isTenantScopeRequired,
    releaseScopeRequestSignal,
    scopeRequestSignal,
} from "../../contexts/tenantScopeHeaders";
import { isDefaultApiOrigin, resolveApiEnvironment } from "../../native/apiEnvironment";
import { PrimeDebugLog } from "../../util/debugLog";
import {
    readScopeNamespaceIndex,
    scopeNamespaceIndexKey,
    writeScopeNamespaceIndex,
} from "../../util/fileCacheScopeIndex";
import { ApiFile } from "../../shared/interfaces";
import { hasServerFileUrl } from "../../shared/pendingFileUrl";
import { fetchViaNativeCache, isNativeViewerAvailable } from "../../native/nativeViewer";
import { getPlatform } from "../../platform/platformRef";
import { e2ePublish, isE2E } from "../../platform/e2e";
import { fetchAuthorizedBytes, isFirstPartyApiUrl } from "../../access/authorizedBytes";
import { CloudDocumentStoreError } from "../../platform/cloudDocumentDownload";
import { CACHE_ROOT_DIR, cachedEntries, reconcileProtectedCache } from "./protectedCacheReconcile";
import { isShopDrawingFile } from "./fileCachePolicy";
import {
    accessLeaseExpired,
    accessTicketIsCurrent,
    captureAccessTicket,
    hasProtectedProject,
} from "../../access/resourceAccess";

/** Native-shell cache path: bytes come from the on-device file via convertFileSrc. The native
 *  cache keys a file by its id alone (`getCachedFile` takes no content hash), so another version
 *  under the same id reads as the same file there (ARCHITECTURE.md, "Invariants"). */
async function loadViaNativeCache(
    file: ApiFile,
    orgSlug: string,
    onProgress: (loadedBytes: number, totalBytes: number) => void,
    signal: AbortSignal | undefined,
    assertCurrent: () => void,
): Promise<File | null> {
    if (signal?.aborted) return null;
    const response = await fetchViaNativeCache({ orgSlug, fileId: file.id, url: file.fileUrl });
    const buffer = await response.arrayBuffer();
    assertCurrent();
    if (signal?.aborted) return null;
    onProgress(buffer.byteLength, buffer.byteLength);
    return new File([buffer], file.fileName, { type: file.fileType || "application/pdf" });
}

/** A file row as the cache reads it: the API's content hash for the bytes `fileUrl` serves. */
type CachedFileRow = ApiFile & { sha256?: string | null };

/** The content hash the row names, or null when the API records none (it sends `null`). */
export function contentSha256(file: CachedFileRow): string | null {
    return typeof file.sha256 === "string" && file.sha256 ? file.sha256 : null;
}

/** File id, sanitized for the entry name (UUIDs are safe, but just in case). */
function safeFileId(file: ApiFile): string {
    return file.id.replace(/[^a-zA-Z0-9_-]/g, "_");
}

/** A value as entry-name characters, case-folded (a hex digest and an origin compare without
 *  case) and otherwise one to one: letters and digits as they are, any other character as `-`
 *  and four hex digits, so a part never holds the `.` or `_` that separate the parts. */
function nameTag(value: string): string {
    return value
        .toLowerCase()
        .replace(/[^a-z0-9]/g, (char) => `-${char.charCodeAt(0).toString(16).padStart(4, "0")}`);
}

/**
 * Generate a cache key for a file: its OPFS entry name, which the in-flight join key
 * (`entryKey`) also names.
 * Format: {first16CharsOfFileName}[.s{sha256}][.o{apiOrigin}]_{fullFileId}
 * - fileName prefix makes it human-readable in OPFS viewer
 * - full file.id ensures uniqueness (not fileUrl which has expiring SAS tokens); it stays last,
 *   where `protectedCacheReconcile` reads it back
 * - one id can carry other bytes (a pinned provider document keeps its anchor id while the API
 *   moves `fileUrl`, `sha256` and `fileSize` to the newest revision), so the content hash the
 *   API sends tells two versions apart. A row without one keeps the name, and the size-only
 *   check, it had before (known gap, ARCHITECTURE.md).
 * - `keyOrigin` is the API origin when the namespace does not name it (a bare organization
 *   folder; a project scope's namespace hashes it), since the native shell's environment picker
 *   points one folder at several origins. The default origin, which every web build resolves,
 *   names nothing, so its entries keep the names they had.
 */
function getCacheKey(file: CachedFileRow, keyOrigin: string | null): string {
    // Sanitize filename for filesystem safety - first 16 chars for readability
    const namePrefix =
        file.fileName
            .replace(/\.[^.]+$/, "") // Remove extension
            .replace(/[^a-zA-Z0-9_-]/g, "_") // Replace unsafe chars
            .substring(0, 16) || "file";
    const parts = [namePrefix];
    const sha256 = contentSha256(file);
    if (sha256) parts.push(`s${nameTag(sha256)}`);
    if (keyOrigin && !isDefaultApiOrigin(keyOrigin)) parts.push(`o${nameTag(keyOrigin)}`);
    return `${parts.join(".")}_${safeFileId(file)}`;
}

/** The API origin part of an entry name for `file` (`getCacheKey`), or null when it names none. */
function entryOriginPart(name: string, file: ApiFile): string | null {
    const parts = name.slice(0, -`_${safeFileId(file)}`.length).split(".");
    return parts.slice(1).find((part) => part.startsWith("o")) ?? null;
}

/** One entry of the store, `{namespace}/{entry name}`: the key a load joins an in-flight
 *  prefetch on (`inFlightPrefetches`) and holds an entry by (`pageHolds`). */
function entryKey(namespace: string, name: string): string {
    return `${namespace}/${name}`;
}

/**
 * This page's holds on store entries (`holdEntry`). The store hands out a `File` that is read
 * later (the engine reads it once it has booted), and OPFS is shared by every tab of the origin,
 * so no tab's write may remove an entry this page has opened, written or is writing while the page
 * is open.
 */
const pageHolds = {
    /** Each entry's (`entryKey`) hold, resolved once granted. Bounded by the files a page opens. */
    entries: new Map<string, Promise<void>>(),
    /** Never settles: each hold's lock callback returns it, so the holds last as long as the page. */
    untilPageCloses: new Promise<never>(() => {}),
};

/** The Web Lock that guards one entry (`entryKey`) across the origin's tabs, as OPFS is shared. */
function entryLockName(key: string): string {
    return `fileviewer-opfs-entry:${key}`;
}

/**
 * Holds the entry `key` across tabs for the rest of this page's life: a shared Web Lock on it,
 * which the browser releases when the page closes. Resolves once the hold is granted, so a load
 * that awaits it before reading the entry lets a removal already under way finish first, and then
 * reads a miss. A host without Web Locks gets no hold and evicts nothing (`removeSupersededEntries`);
 * a refused request (an origin without storage access, which refuses the removal's lock too) leaves
 * the entry unheld and the load going.
 */
function holdEntry(key: string): Promise<void> {
    const existing = pageHolds.entries.get(key);
    if (existing) return existing;
    const locks = globalThis.navigator?.locks;
    const held = new Promise<void>((granted) => {
        if (!locks) return granted();
        const unheld = (err: unknown) => {
            PrimeDebugLog("loadViaOpfs", `Holding ${key} across tabs failed:`, err);
            granted();
        };
        try {
            locks
                .request(entryLockName(key), { mode: "shared" }, () => {
                    granted();
                    return pageHolds.untilPageCloses;
                })
                .catch(unheld);
        } catch (err) {
            unheld(err);
        }
    });
    pageHolds.entries.set(key, held);
    return held;
}

/** One namespace folder's entry names as this page knows them (`namespaceEntries`), by file id. */
interface KnownEntries {
    /** Settles once the folder's one listing is in `byFileId` (an empty one if it failed). */
    listed: Promise<void>;
    /** Each name under its `fileIdBucket`. */
    byFileId: Map<string, Set<string>>;
}

/**
 * This page's view of each namespace folder's entries, for `removeSupersededEntries`: a folder is
 * listed once, on the page's first removal pass there, and a pass after that reads only the copies
 * of its own file id, so N writes cost one listing and O(1) each rather than a listing each
 * (quadratic over a prefetch burst). This page's own writes and removals keep the view current;
 * another tab's do not reach it, so it can miss a copy written since (left for a later page to
 * remove) or still name one removed since (its removal finds nothing). Every name in it was listed
 * or written in the folder, so it never leads to a removal a fresh listing would not, and each
 * removal still asks for the entry's lock. Bounded by the folder's entries; a folder's removal
 * (`clearFileCacheForTenant`) drops its view.
 */
const namespaceEntries = new Map<string, KnownEntries>();

/** The `byFileId` bucket of an entry name, or of a file's `_{safeFileId}` suffix: the part after
 *  its last `_`, which is the whole file id for a UUID. A file id with a `_` shares its bucket with
 *  others, which `removeSupersededEntries`'s suffix check tells apart. */
function fileIdBucket(nameOrSuffix: string): string {
    return nameOrSuffix.slice(nameOrSuffix.lastIndexOf("_") + 1);
}

/** `namespace`'s view, listing the folder the first time this page asks for it. */
function knownEntries(tenantDir: FileSystemDirectoryHandle, namespace: string): KnownEntries {
    const existing = namespaceEntries.get(namespace);
    if (existing) return existing;
    const byFileId = new Map<string, Set<string>>();
    const listed = cachedEntries(tenantDir).then(
        (names) => names.forEach((name) => noteEntry(byFileId, name, true)),
        (err: unknown) => PrimeDebugLog("loadViaOpfs", "Listing superseded copies failed:", err),
    );
    const known = { listed, byFileId };
    namespaceEntries.set(namespace, known);
    return known;
}

/** Adds `name` to, or drops it from, a view's `byFileId`; a no-op for a folder not listed yet. */
function noteEntry(
    byFileId: Map<string, Set<string>> | undefined,
    name: string,
    present: boolean,
): void {
    if (!byFileId) return;
    const bucket = fileIdBucket(name);
    const names = byFileId.get(bucket);
    if (present) byFileId.set(bucket, (names ?? new Set()).add(name));
    else if (names?.delete(name) && names.size === 0) byFileId.delete(bucket);
}

/**
 * After a write of `written`, or on this page's first cache hit of it, removes the namespace's
 * other entries for the same file id under the same API origin: an earlier version of its bytes
 * (another sha256), its copy from before the content was named, or one under an earlier file
 * name. It finds them in this page's view of the folder (`namespaceEntries`), not a listing. An
 * entry any open tab holds (`holdEntry`), this one included, stays: each removal runs under the
 * entry's exclusive lock, asked for only if it is free at once. Without Web Locks no tab can tell
 * whether another holds an entry, so nothing is listed or removed. Only a row that names its
 * content calls this: one without a sha256 cannot say which copy is current, and removes nothing,
 * as before. Never throws.
 */
async function removeSupersededEntries(
    tenantDir: FileSystemDirectoryHandle,
    namespace: string,
    file: CachedFileRow,
    written: string,
): Promise<void> {
    const locks = globalThis.navigator?.locks;
    if (!locks) return;
    const suffix = `_${safeFileId(file)}`;
    const origin = entryOriginPart(written, file);
    const known = knownEntries(tenantDir, namespace);
    await known.listed;
    for (const name of Array.from(known.byFileId.get(fileIdBucket(suffix)) ?? [])) {
        if (name === written || !name.endsWith(suffix)) continue;
        if (entryOriginPart(name, file) !== origin) continue;
        await locks
            .request(
                entryLockName(entryKey(namespace, name)),
                { mode: "exclusive", ifAvailable: true },
                // No lock: a tab holds the entry, or is waiting to read it.
                async (lock) => {
                    if (!lock) return;
                    // One that is gone already (another tab removed it) leaves the rest to remove.
                    await tenantDir.removeEntry(name).catch((err: unknown) => {
                        if ((err as { name?: unknown } | null)?.name !== "NotFoundError") throw err;
                    });
                    noteEntry(known.byFileId, name, false);
                },
            )
            .catch((err: unknown) => {
                PrimeDebugLog("loadViaOpfs", `Removing superseded copy ${name} failed:`, err);
            });
    }
}

export interface LoadFileOptions {
    signal?: AbortSignal;
    /** Skip native/OPFS persistence for sources without a durable entity/version id. A shop
     *  drawing (`isShopDrawingFile`) skips it whatever this says. */
    disableCache?: boolean;
}

/** `Uint8Array.buffer` is typed `ArrayBufferLike` (it can back onto a
 *  `SharedArrayBuffer`), but `BlobPart` requires a real `ArrayBuffer` -- slices only
 *  when the view does not already cover a whole, dedicated one. */
function toBlobBuffer(bytes: Uint8Array): ArrayBuffer {
    if (!(bytes.buffer instanceof ArrayBuffer)) return Uint8Array.from(bytes).buffer;
    return bytes.byteOffset === 0 && bytes.byteLength === bytes.buffer.byteLength
        ? bytes.buffer
        : bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength);
}

/** A direct PDF engine source must own one complete normal ArrayBuffer. A view or
 * SharedArrayBuffer-backed value gets exactly one bounded normalization copy here. */
function toDedicatedPdfBytes(bytes: Uint8Array): Uint8Array {
    if (
        bytes.buffer instanceof ArrayBuffer &&
        bytes.byteOffset === 0 &&
        bytes.byteLength === bytes.buffer.byteLength
    ) {
        return new Uint8Array(bytes.buffer);
    }
    return Uint8Array.from(bytes);
}

export type LoadedFileSource =
    { kind: "file"; file: File } | { kind: "bytes"; bytes: Uint8Array; sha256: string };

/** Why a load handed over no bytes. `aborted`: the caller's signal or a project change
 *  ended it. `denied`: this member may not read the file now (access changed, or the server
 *  refused, lacks or never described it); neither is a reason to reach the file another way.
 *  `unavailable`: the bytes could not be fetched (an HTTP error, the cloud store, the body). */
export type LoadFileFailure =
    | { kind: "aborted" }
    | { kind: "denied"; reason: "access-changed" | "not-available" }
    | { kind: "unavailable"; reason: "http" | "cloud-store" | "body"; status?: number };

export type LoadFileOutcome = LoadedFileSource | LoadFileFailure;

const ABORTED: LoadFileFailure = { kind: "aborted" };
const ACCESS_CHANGED: LoadFileFailure = { kind: "denied", reason: "access-changed" };
const NOT_AVAILABLE: LoadFileFailure = { kind: "denied", reason: "not-available" };

function isAbortError(error: unknown): boolean {
    return (error as { name?: unknown } | null)?.name === "AbortError";
}

/** An API URL's 403 or 404 reads as a file hidden from this member; any other HTTP failure leaves
 *  the bytes unavailable. An API URL's 401 never gets here: the transport's admission fence
 *  (`observeResponseAdmission`) throws first. */
function httpFailure(url: string, status: number): LoadFileFailure {
    return isFirstPartyApiUrl(url) && [403, 404].includes(status)
        ? NOT_AVAILABLE
        : { kind: "unavailable", reason: "http", status };
}

function cloudStoreFailure(error: unknown): LoadFileFailure {
    if (isAbortError(error)) return ABORTED;
    if (error instanceof CloudDocumentStoreError && ["auth", "notFound"].includes(error.kind))
        return NOT_AVAILABLE;
    return { kind: "unavailable", reason: "cloud-store" };
}

/** E2E-only ownership marks for the local source lane. A `File` has no explicit
 * close API and JS cannot observe Blob/GC copies, so the marks distinguish the
 * copies we control from implementation-defined ownership after construction. */
function markLocalByteStage(fileId: string, stage: string, detail: Record<string, unknown>): void {
    if (isE2E()) e2ePublish.mark(`bytes:local:${stage}`, { fileId, ...detail });
}

// evictSupersededScopeNamespace's memo: after a principal+project's first load in this page,
// each further one (hundreds on a 900-sheet set) is a Map hit, not a localStorage round trip.
const knownScopeNamespaces = new Map<string, string>();

/**
 * A role change mints a new OPFS namespace for the same principal+project (`cacheOperation`'s
 * `scope-<sha256>`): this evicts the previous namespace's directory, which would otherwise sit
 * there duplicated until logout. The index is keyed by origin, principal and project, so it only
 * ever evicts this principal's own earlier namespace for this project.
 */
async function evictSupersededScopeNamespace(
    principalId: string,
    projectId: string,
    namespace: string,
): Promise<void> {
    const memoKey = `${principalId}:${projectId}`;
    if (knownScopeNamespaces.get(memoKey) === namespace) return;
    knownScopeNamespaces.set(memoKey, namespace);

    const index = readScopeNamespaceIndex();
    const storageKey = scopeNamespaceIndexKey(principalId, projectId);
    const previous = index[storageKey];
    if (previous === namespace) return;
    index[storageKey] = namespace;
    writeScopeNamespaceIndex(index);
    if (!previous) return;

    try {
        await clearFileCacheForTenant(previous);
        PrimeDebugLog(
            "evictSupersededScopeNamespace",
            `Evicted superseded namespace ${previous} for ${storageKey}`,
        );
    } catch (err) {
        PrimeDebugLog(
            "evictSupersededScopeNamespace",
            `Failed to evict namespace ${previous}:`,
            err,
        );
    }
}

async function cacheOperation(orgSlug: string | null, signal?: AbortSignal) {
    const boundary = captureTenantScopeBoundary();
    const scoped = isTenantScopeRequired();
    const key = currentTenantContentScopeKey();
    const effectiveSignal = scopeRequestSignal(signal);
    const assertCurrent = () => {
        try {
            boundary.assertCurrent();
            if (effectiveSignal?.aborted || (scoped && !key)) throw new Error("Unavailable");
        } catch {
            throw new DOMException("Project changed or request cancelled", "AbortError");
        }
    };
    assertCurrent();
    const apiOrigin = resolveApiEnvironment().origin;
    // No guessed legacy-to-company alias. Previously downloaded canonical
    // bytes reopen only under the exact admitted content identity.
    let namespace = orgSlug;
    if (scoped) {
        namespace = crypto.subtle
            ? "scope-" +
              Array.from(
                  new Uint8Array(
                      await crypto.subtle.digest(
                          "SHA-256",
                          new TextEncoder().encode(JSON.stringify([apiOrigin, key!])),
                      ),
                  ),
                  (value) => value.toString(16).padStart(2, "0"),
              ).join("")
            : null;
        const activeScope = currentTenantScope();
        if (namespace && activeScope) {
            await evictSupersededScopeNamespace(
                activeScope.principalId,
                activeScope.projectId,
                namespace,
            );
            // D8: a policy change reaches the cache before any of it is read or extended.
            await reconcileProtectedCache(
                namespace,
                activeScope.principalId,
                activeScope.projectId,
            );
        }
    }
    assertCurrent();
    // A project scope's namespace names the API origin; a bare organization folder's entries
    // name it instead (`getCacheKey`).
    const keyOrigin = scoped ? null : apiOrigin;
    return { namespace, keyOrigin, signal: effectiveSignal, assertCurrent };
}

/**
 * An `ApiFile`'s bytes, from the first source that has them (a local source, the desktop's cloud
 * store, the native cache, OPFS, or the network into memory), reporting progress and cancelled by
 * `signal`: an owned byte source or a `File`, or the `LoadFileFailure` that says why there are
 * none. OPFS entries live under the admitted tenant content scope.
 */
export async function loadFileSourceAndCache(
    file: ApiFile & { sha256?: string },
    orgSlug: string | null,
    onProgress: (loadedBytes: number, totalBytes: number) => void,
    options: LoadFileOptions,
): Promise<LoadFileOutcome> {
    let operation: Awaited<ReturnType<typeof cacheOperation>> | undefined;
    try {
        operation = await cacheOperation(orgSlug, options.signal);
        const progress = (loaded: number, total: number) => {
            operation!.assertCurrent();
            onProgress(loaded, total);
        };
        const result = await loadSourceInScope(
            file,
            operation.namespace,
            operation.keyOrigin,
            progress,
            {
                signal: operation.signal,
                disableCache: !!options.disableCache || isShopDrawingFile(file),
            },
            operation.assertCurrent,
        );
        operation.assertCurrent();
        return result;
    } catch (error: unknown) {
        if (error instanceof DOMException && error.name === "AbortError") return ABORTED;
        throw error;
    } finally {
        // Released either way, or repeated opens pile up listeners without `AbortSignal.any`.
        releaseScopeRequestSignal(operation?.signal);
    }
}

async function loadSourceInScope(
    file: ApiFile & { sha256?: string },
    orgSlug: string | null,
    keyOrigin: string | null,
    onProgress: (loadedBytes: number, totalBytes: number) => void,
    { signal, disableCache = false }: LoadFileOptions,
    assertCurrent: () => void,
): Promise<LoadFileOutcome> {
    assertCurrent();
    const platform = getPlatform();
    // A local file (Open PDF…, This Mac, drag-drop, a file:// intent): never a cloud lookup.
    // `localSource` is unset on every server-read `ApiFile`.
    if (file.localSource) {
        if (signal?.aborted) return ABORTED;
        if (isE2E()) e2ePublish.bytesSource(file.id, "local");
        if (file.localSource.path && platform.files?.read) {
            markLocalByteStage(file.id, "read-start", { knownJsWholeFileCopies: 0 });
            const localFile = await platform.files.read(file.localSource.path);
            if (signal?.aborted) return ABORTED;
            markLocalByteStage(file.id, "read-complete", {
                bytes: localFile.bytes.byteLength,
                knownJsWholeFileCopies: 0,
            });
            onProgress(localFile.bytes.byteLength, localFile.bytes.byteLength);
            const bytes = toDedicatedPdfBytes(localFile.bytes);
            const normalizedCopy = bytes.buffer !== localFile.bytes.buffer;
            markLocalByteStage(file.id, "direct-source-ready", {
                bytes: bytes.byteLength,
                knownJsWholeFileCopies: normalizedCopy ? 1 : 0,
                normalizedCopy,
            });
            markLocalByteStage(file.id, "source-view-release-eligible", {
                bytes: bytes.byteLength,
            });
            return { kind: "bytes", bytes, sha256: localFile.sha256 };
        }
        // Already-resident bytes (drag-drop, the file picker): `fileUrl` is an object URL.
        return fetchToMemory(file, onProgress, signal, assertCurrent);
    }

    // A protected project serves cached bytes only for a file the server just described (a
    // signed link), never a bare id; checked before every persistent cache, since the desktop
    // store and the native cache answer from a file id.
    if (!disableCache && hasProtectedProject() && !hasServerFileUrl(file)) return NOT_AVAILABLE;

    // Protected projects cache like any other through 7-day signed links (D8). An `/api/ai` URL
    // (an access-gateway fallback, a Document Compare PDF, a Marvin eval asset) is point-checked:
    // one authenticated GET into memory, never cached, discarded if project access changes
    // meanwhile. The persistent caches below only ever see a URL that carries its own authority.
    if (isFirstPartyApiUrl(file.fileUrl)) {
        const accessTicket = captureAccessTicket();
        const accessChanged = () => !accessTicketIsCurrent(accessTicket) || accessLeaseExpired();
        const check = () => {
            assertCurrent();
            if (accessChanged()) throw new Error("Project access changed during download");
        };
        if (isE2E()) e2ePublish.bytesSource(file.id, "network");
        try {
            const loaded = await fetchToMemory(file, onProgress, signal, check);
            return accessChanged() ? ACCESS_CHANGED : loaded;
        } catch (error) {
            if (!isAbortError(error) && accessChanged()) return ACCESS_CHANGED;
            throw error;
        }
    }

    const cloudDocuments = platform.offline?.cloudDocuments;
    if (!disableCache && cloudDocuments) {
        try {
            const loaded = await cloudDocuments.load(file.documentId ?? file.id, {
                expectedSha256: file.sha256,
                expectedName: file.fileName,
                sourceFileId: file.id,
                downloadUrl: file.fileUrl,
                signal,
            });
            if (signal?.aborted) return ABORTED;
            if (isE2E())
                e2ePublish.bytesSource(file.id, loaded.source === "cache" ? "store" : "network");
            onProgress(loaded.bytes.byteLength, loaded.bytes.byteLength);
            return {
                kind: "file",
                file: new File([toBlobBuffer(loaded.bytes)], file.fileName, {
                    type: file.fileType || "application/pdf",
                }),
            };
        } catch (err) {
            PrimeDebugLog("loadFileAndCache", "Cloud document load failed", err);
            return cloudStoreFailure(err);
        }
    }

    // Native shell: OPFS in WKWebView is unreliable, so the native layer caches on-device.
    if (!disableCache && isNativeViewerAvailable() && orgSlug && file.fileUrl) {
        try {
            const nativeFile = await loadViaNativeCache(
                file,
                orgSlug,
                onProgress,
                signal,
                assertCurrent,
            );
            if (nativeFile) {
                if (isE2E()) e2ePublish.bytesSource(file.id, "store");
                return { kind: "file", file: nativeFile };
            }
        } catch (err) {
            assertCurrent();
            PrimeDebugLog("loadFileAndCache", "Native cache failed — falling back", err);
        }
    }

    // Only use OPFS caching when enabled and we have a valid org slug.
    // This prevents caching files under an "unknown" tenant.
    if (!disableCache && supportsOpfs() && orgSlug) {
        // If a background prefetch of this exact file is mid-flight, wait for
        // it so we hit its cache entry instead of downloading in parallel.
        const pendingPrefetch = inFlightPrefetches.get(
            entryKey(orgSlug, getCacheKey(file, keyOrigin)),
        );
        if (pendingPrefetch) await pendingPrefetch;
        assertCurrent();
        try {
            const opfs = await loadViaOpfs(
                file,
                orgSlug,
                keyOrigin,
                onProgress,
                signal,
                assertCurrent,
            );
            if (opfs) {
                if (isE2E()) e2ePublish.bytesSource(file.id, opfs.fromCache ? "opfs" : "network");
                return { kind: "file", file: opfs.file };
            }
        } catch (err: any) {
            if (err.name === "AbortError") {
                PrimeDebugLog("loadFileAndCache", "OPFS load aborted");
                return ABORTED;
            }
            PrimeDebugLog("loadFileAndCache", "OPFS failed — falling back", err);
        }
    } else if (disableCache) {
        PrimeDebugLog("loadFileAndCache", "Persistent cache disabled — using memory");
    } else if (!orgSlug) {
        PrimeDebugLog("loadFileAndCache", "No org slug — skipping cache, using memory");
    } else {
        PrimeDebugLog("loadFileAndCache", "OPFS unavailable — using memory");
    }

    assertCurrent();
    // OPFS unsupported, no org, or failed: do memory fetch
    if (isE2E()) e2ePublish.bytesSource(file.id, "network");
    return fetchToMemory(file, onProgress, signal, assertCurrent);
}

/** For callers that need a browser `File`; null on any `LoadFileFailure` (a failed download to
 *  them). A local desktop open keeps `loadFileSourceAndCache`'s direct bytes instead. */
export async function loadFileAndCache(
    file: ApiFile & { sha256?: string },
    orgSlug: string | null,
    onProgress: (loadedBytes: number, totalBytes: number) => void,
    options: LoadFileOptions,
): Promise<File | null> {
    const source = await loadFileSourceAndCache(file, orgSlug, onProgress, options);
    if (source.kind === "file") return source.file;
    if (source.kind !== "bytes") return null;
    return new File([toBlobBuffer(source.bytes)], file.fileName, {
        type: file.fileType || "application/pdf",
    });
}

function supportsOpfs(): boolean {
    return (
        typeof navigator.storage?.getDirectory === "function" &&
        typeof FileSystemFileHandle !== "undefined"
    );
}

// In-flight prefetches, keyed by the entry they fill (`entryKey`: the namespace and the content
// the row names) so re-renders don't start duplicate downloads and the main loader can join one
// for the same bytes instead of racing it, and never one for another version under the same id.
const inFlightPrefetches = new Map<string, Promise<void>>();

/**
 * Warms the cache the open path reads on this host (the desktop's cloud store, else OPFS), so a
 * later open is a hit. A no-op without either, for a file already prefetching, an `/api/ai` URL
 * or a shop drawing. Never throws, and runs to completion after the caller moves on.
 */
export async function prefetchFileToCache(
    file: ApiFile & { sha256?: string },
    orgSlug: string | null,
): Promise<void> {
    if (isFirstPartyApiUrl(file.fileUrl) || isShopDrawingFile(file)) return;
    const cloudDocuments = getPlatform().offline?.cloudDocuments;
    if (!file.fileUrl || (!cloudDocuments && !supportsOpfs())) return;

    // The namespace an open would read, abandoned if the project changes (PP-827).
    let operation: Awaited<ReturnType<typeof cacheOperation>> | undefined;
    try {
        operation = await cacheOperation(orgSlug);
        if (!operation.namespace) return;

        const key = entryKey(operation.namespace, getCacheKey(file, operation.keyOrigin));
        const existing = inFlightPrefetches.get(key);
        if (existing) return existing;

        const warm = cloudDocuments
            ? cloudDocuments.load(file.documentId ?? file.id, {
                  expectedSha256: file.sha256,
                  expectedName: file.fileName,
                  sourceFileId: file.id,
                  downloadUrl: file.fileUrl,
                  signal: operation.signal,
              })
            : loadViaOpfs(
                  file,
                  operation.namespace,
                  operation.keyOrigin,
                  () => {},
                  operation.signal,
                  operation.assertCurrent,
              );
        const task: Promise<void> = warm
            .then(() => undefined)
            .catch((err) =>
                PrimeDebugLog("prefetchFileToCache", `Prefetch failed for ${key}:`, err),
            )
            .finally(() => inFlightPrefetches.delete(key));
        inFlightPrefetches.set(key, task);
        await task;
    } catch (error) {
        PrimeDebugLog("prefetchFileToCache", "Prefetch scope ended", error);
    } finally {
        releaseScopeRequestSignal(operation?.signal);
    }
}

/** Clears one tenant's OPFS cache directory. */
export async function clearFileCacheForTenant(orgSlug: string): Promise<void> {
    if (!navigator?.storage?.getDirectory) {
        return;
    }
    // The next removal pass there lists the folder again, whatever this removal finds.
    namespaceEntries.delete(orgSlug);

    try {
        await navigator?.storage?.persist?.();
        const root = await navigator.storage.getDirectory();
        const cacheRoot = await root.getDirectoryHandle(CACHE_ROOT_DIR, { create: false });

        await cacheRoot.removeEntry(orgSlug, { recursive: true });
        PrimeDebugLog("clearFileCacheForTenant", `Removed cache for tenant: ${orgSlug}`);
    } catch (err: any) {
        if (err.name === "NotFoundError") {
            // Directory doesn't exist, nothing to clear
            PrimeDebugLog("clearFileCacheForTenant", `No cache found for tenant: ${orgSlug}`);
        } else {
            PrimeDebugLog("clearFileCacheForTenant", `Failed to clear cache for ${orgSlug}:`, err);
        }
    }
}

async function getTenantDirectory(
    root: FileSystemDirectoryHandle,
    orgSlug: string,
): Promise<FileSystemDirectoryHandle> {
    const cacheRoot = await root.getDirectoryHandle(CACHE_ROOT_DIR, { create: true });
    return await cacheRoot.getDirectoryHandle(orgSlug, { create: true });
}

/**
 * OPFS-based loader with tenant isolation. Returns null on any non-AbortError failure.
 * Throws only if `signal` is aborted. `fromCache` tells a cache hit from a download
 * that landed in the cache.
 */
async function loadViaOpfs(
    file: CachedFileRow,
    orgSlug: string,
    keyOrigin: string | null,
    onProgress: (loadedBytes: number, totalBytes: number) => void,
    signal: AbortSignal | undefined,
    assertCurrent: () => void,
): Promise<{ file: File; fromCache: boolean } | null> {
    // The entry is this page's from here on, held before any read of it (`holdEntry`).
    const safeName = getCacheKey(file, keyOrigin);
    const firstOpen = !pageHolds.entries.has(entryKey(orgSlug, safeName));
    await holdEntry(entryKey(orgSlug, safeName));
    await navigator.storage.persist?.();
    assertCurrent();
    const root = await navigator.storage.getDirectory();
    assertCurrent();
    const tenantDir = await getTenantDirectory(root, orgSlug);

    assertCurrent();

    // Try cache-hit; swallow errors as "miss"
    const existingHandle = await tenantDir
        .getFileHandle(safeName, { create: false })
        .catch(() => null);
    if (existingHandle) {
        const cached = await existingHandle.getFile();
        assertCurrent();
        // Validate cached file size matches expected size to detect truncated/corrupt files
        const expectedSize = file.fileSize ?? 0;
        if (cached.size > 0 && (expectedSize === 0 || cached.size === expectedSize)) {
            PrimeDebugLog(
                "loadViaOpfs",
                `Cache hit for ${orgSlug}/${safeName} (${cached.size} bytes) - returning immediately`,
            );
            // The copies of another version an earlier page kept (it had handed them out) go on
            // this page's first open of named content, off the open's path: OPFS evicts nothing
            // by size, and a later write of this version may never come.
            if (firstOpen && contentSha256(file))
                void removeSupersededEntries(tenantDir, orgSlug, file, safeName);
            return { file: cached, fromCache: true };
        }
        if (cached.size > 0 && cached.size !== expectedSize) {
            PrimeDebugLog(
                "loadViaOpfs",
                `Cache size mismatch: cached=${cached.size}, expected=${expectedSize} → re-download`,
            );
        } else {
            PrimeDebugLog("loadViaOpfs", "Empty cache → re-download");
        }
    } else {
        PrimeDebugLog("loadViaOpfs", `Cache miss for ${orgSlug}/${safeName} → download`);
    }

    // A file described with no link has nothing to download; the memory path says why.
    if (!file.fileUrl) return null;
    assertCurrent();
    const handle = await tenantDir.getFileHandle(safeName, { create: true });
    // @ts-ignore -- OPFS not fully in TS yet
    if (typeof handle.createWritable !== "function") {
        PrimeDebugLog("loadViaOpfs", "No OPFS write → memory fallback");
        return null;
    }

    assertCurrent();
    const resp = await fetchAuthorizedBytes(file.fileUrl, { signal });
    assertCurrent();
    if (!resp.ok) {
        PrimeDebugLog("loadViaOpfs", `Fetch failed: ${resp.status} ${resp.statusText}`);
        return null;
    }

    if (!resp.body) {
        PrimeDebugLog(
            "loadViaOpfs",
            "No body stream (non-streaming response?) — using memory fallback",
        );
        return null;
    }

    const totalBytes = Number(resp.headers.get("Content-Length") || 0);
    let loadedBytes = 0;

    onProgress(0, totalBytes);

    // A timeout keeps a stuck createWritable from hanging the load.
    const reader = resp.body.getReader();

    // @ts-ignore -- OPFS createWritable not in TS types yet
    let writable: any;
    try {
        writable = await Promise.race([
            (handle as any).createWritable(),
            new Promise((_, reject) =>
                setTimeout(() => reject(new Error("OPFS createWritable timeout")), 5000),
            ),
        ]);
    } catch (err) {
        await reader.cancel().catch(() => {});
        PrimeDebugLog("loadViaOpfs", "createWritable failed, falling back:", err);
        return null;
    }

    try {
        assertCurrent();
        // The pump loop; throws on abort.
        while (true) {
            if (signal?.aborted) throw new DOMException("Aborted", "AbortError");
            const { done, value } = await reader.read();
            assertCurrent();
            if (done) break;

            loadedBytes += value.byteLength;
            onProgress(loadedBytes, totalBytes);
            await (writable as any).write(value);
        }

        assertCurrent();
        await (writable as any).close();
        assertCurrent();
    } catch (err: any) {
        await Promise.allSettled([
            (writable as any).abort?.().catch(() => {}),
            reader.cancel().catch(() => {}),
        ]);
        // An abort keeps the partial entry: the user may switch back, and a removal would race
        // the new download. Any other failure removes the truncated file and falls back to memory.
        if (err?.name === "AbortError") throw err;
        await tenantDir.removeEntry(safeName).catch(() => {});
        noteEntry(namespaceEntries.get(orgSlug)?.byFileId, safeName, false);
        PrimeDebugLog("loadViaOpfs", "Write failed, falling back to memory:", err);
        return null;
    }

    const finalFile = await handle.getFile();
    assertCurrent();
    onProgress(finalFile.size, totalBytes || finalFile.size);
    noteEntry(namespaceEntries.get(orgSlug)?.byFileId, safeName, true);
    if (contentSha256(file)) await removeSupersededEntries(tenantDir, orgSlug, file, safeName);
    return { file: finalFile, fromCache: false };
}

/**
 * In-memory fallback. A file the server described with no link is not available, on every path,
 * rather than a GET of the app's own page.
 */
async function fetchToMemory(
    file: ApiFile,
    onProgress: (loadedBytes: number, totalBytes: number) => void,
    signal: AbortSignal | undefined,
    assertCurrent: () => void,
): Promise<LoadFileOutcome> {
    if (!file.fileUrl) return NOT_AVAILABLE;
    const resp = await fetchAuthorizedBytes(file.fileUrl, { signal }).catch((err) => {
        if (err.name === "AbortError") {
            PrimeDebugLog("fetchToMemory", "Aborted");
            return null;
        }
        throw err;
    });

    assertCurrent();
    if (!resp) {
        return ABORTED;
    }
    if (!resp.ok) {
        PrimeDebugLog("fetchToMemory", `Fetch failed: ${resp.status} ${resp.statusText}`);
        return httpFailure(file.fileUrl, resp.status);
    }

    const totalBytes = Number(resp.headers.get("Content-Length") || 0);
    let loadedBytes = 0;

    onProgress(0, totalBytes);

    // A non-streaming response (204, HEAD, a consumed body).
    if (!resp.body) {
        PrimeDebugLog(
            "fetchToMemory",
            "No body stream (non-streaming response?) — using arrayBuffer fallback",
        );
        try {
            const buffer = await resp.arrayBuffer();
            assertCurrent();
            const blob = new Blob([buffer], {
                type: resp.headers.get("Content-Type") || undefined,
            });
            onProgress(buffer.byteLength, totalBytes || buffer.byteLength);
            return { kind: "file", file: new File([blob], file.fileName, { type: blob.type }) };
        } catch (err: any) {
            if (err?.name === "AbortError") {
                PrimeDebugLog("fetchToMemory", "arrayBuffer fetch aborted");
                return ABORTED;
            }
            PrimeDebugLog("fetchToMemory", "arrayBuffer fetch failed:", err);
            return { kind: "unavailable", reason: "body" };
        }
    }

    const reader = resp.body.getReader();
    const chunks: Uint8Array[] = [];

    while (true) {
        if (signal?.aborted) {
            await reader.cancel().catch(() => {});
            return ABORTED;
        }

        const { done, value } = await reader.read();
        assertCurrent();
        if (done) {
            break;
        }

        chunks.push(value!);
        loadedBytes += value.byteLength;
        onProgress(loadedBytes, totalBytes);
    }

    // Every chunk is a network Uint8Array over a plain ArrayBuffer (a cast for TS >= 5.7).
    const blob = new Blob(chunks as unknown as BlobPart[], {
        type: resp.headers.get("Content-Type") || undefined,
    });
    onProgress(loadedBytes, totalBytes || loadedBytes);
    return { kind: "file", file: new File([blob], file.fileName, { type: blob.type }) };
}
